/*  ========================================================================
    # Components - Lazy Image
    ========================================================================  */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import useLocalization from '../hooks/useLocalization';
import LazySource from './LazySource';

const LazyImage = ({
  figureClass,
  imageClass,
  children,
  defaultUrl,
  placeholderUrl,
  alt,
  onClick,
  hasEnlarge = false
}) => {
  const imageElmRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Icons & Placeholder
  const local = useLocalization();
  const iconEnlarge = `${local('svgSprite')}#zoom`;
  const defaultPlaceholder = placeholderUrl
    ? placeholderUrl
    : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPs+Q8AAh0BjeU/5JAAAAAASUVORK5CYII=';

  const sourceElements = (items, isVisible) => {
    if (!items) {
      return null;
    } else {
      return Array.isArray(items)
        ? items.map((child, index) => React.cloneElement(child, { key: index, visible: isVisible }))
        : React.cloneElement(items, { visible: isVisible });
    }
  };

  const { ref, inView } = useInView({
    rootMargin: '600px',
    threshold: 0,
    triggerOnce: true
  });

  useEffect(() => {
    imageElmRef.current?.addEventListener('load', setImageLoaded(true));
    return () => imageElmRef.current?.removeEventListener('load', setImageLoaded(true));
  }, [imageElmRef.current]);

  return (
    <figure
      ref={ref}
      onClick={onClick}
      style={{ backgroundImage: `url(${defaultPlaceholder})` }}
      className={[
        'lazy-image',
        imageLoaded ? '' : 'lazy-image--pending',
        !inView && !imageLoaded && !placeholderUrl ? 'lazy-image--loading' : '',
        figureClass
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className='lazy-image__loader' />
      <picture>
        {sourceElements(children, inView)}
        <img
          className={['lazy-image__img', imageClass].filter(Boolean).join(' ')}
          src={inView && imageLoaded ? defaultUrl : defaultPlaceholder}
          alt={alt}
          ref={imageElmRef}
        />
      </picture>
      {hasEnlarge && (
        <div className='c-expandable-image__enlarge'>
          <svg viewBox='0 0 200 200'>
            <use href={iconEnlarge}></use>
          </svg>
        </div>
      )}
    </figure>
  );
};

LazyImage.propTypes = {
  figureClass: PropTypes.string,
  imageClass: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.objectOf(LazySource), PropTypes.arrayOf(LazySource)]),
  defaultUrl: PropTypes.string,
  placeholderUrl: PropTypes.string,
  alt: PropTypes.string,
  onClick: PropTypes.func,
  hasEnlarge: PropTypes.bool
};

export default LazyImage;
